import { styled } from '@/stitches.config';
import { Button, Input, Label, Select } from '@/common/components';
import { FormEvent, useState } from 'react';
import {
  AddCampaignFormStringValues,
  AddCampaignValidationFields,
  AddCampaignValueErrors,
  IVolumeDistributionAsString,
} from './AddCampaignForm.types';
import { Sizes, Variants } from '@/common/types';
import { InputDateSelector } from '@/common/components/builder/organisms/InputDateSelector';
import {
  addCampaignValidationConfig,
  getCopies,
  getUiElementsToShow,
  transformCampaignFormStringValuesToCampaignFormValues,
  validateForm,
  validateVolumeDistributionItem,
} from './AddCampaignForm.helper';
import { getCampaignMediumItems } from '../Campaign.helper';
import { StyledSpinnerIcon } from '@/modules/genetic-counseling/components/AppointmentScheduler/AppointmentScheduler.styles';
import { VolumeDistribution } from './components/VolumeDistribution';
import { TFunction } from 'i18next';

const LoadingContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const RootContainer = styled('section', {
  padding: '0 $12',
});

const FlexColContainer = styled('div', {
  marginBottom: '$8',
  display: 'flex',
  flexDirection: 'column',
  gap: '$12',
});

const RowContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
});

const URLContainer = styled('div', {
  marginTop: '$10',
  border: '1px solid $seafoam400',
  padding: '$4 $3',
});

const ButtonsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const VolumeDistributionContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
});

export interface ShowItemsObj {
  all?: boolean;
  volumeDistribution?: boolean;
  dates?: boolean;
  campaignInfo?: boolean;
}

interface Props {
  onSubmit?: (values: AddCampaignFormStringValues) => void;
  onCancel?: () => void;
  campaignMediums?: Array<{ id: string; label: string }>;
  url?: string;
  isLoading?: boolean;
  values?: AddCampaignFormStringValues;
  t: TFunction;
  showItems?: Array<keyof ShowItemsObj>;
  validateFormFn?: ({ t }: { t: TFunction }) => AddCampaignValidationFields;
  mainButtonLabel?: string;
}

export const AddCampaignForm = ({
  onSubmit,
  onCancel,
  url,
  isLoading,
  values: initialValues,
  t,
  showItems = ['all'],
  validateFormFn = addCampaignValidationConfig,
  mainButtonLabel,
}: Props): JSX.Element => {
  const copies = getCopies({ t });
  const campaignMedium = getCampaignMediumItems(t);
  const visibleElements = getUiElementsToShow(showItems);

  // if all keys are undefined it means there are no errors
  const [errors, setErrors] = useState<AddCampaignValueErrors>({});
  // This are the form values, we store them to format the URL
  const [values, setValues] = useState<AddCampaignFormStringValues | null>(initialValues ?? { volumeDistribution: [] });

  const handleSetValue = (event: FormEvent<HTMLInputElement>, key: keyof AddCampaignFormStringValues): void => {
    const currentTarget = event.target as unknown as { value?: string };
    const value = currentTarget.value;
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleInputDateValues = (newValue: string | null, key: keyof AddCampaignFormStringValues): void => {
    if (newValue) {
      setValues((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    }
  };

  const handleChangeSelect = (id: string, key: keyof AddCampaignFormStringValues): void => {
    if (!campaignMedium) {
      return;
    }
    const value = campaignMedium.find((value) => value.id === id);
    if (value) {
      setValues((prev) => ({
        ...prev,
        [key]: value.id,
      }));
    }
  };

  const handleAddVolumeDistribution = (event: React.SyntheticEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setValues((prev) => {
      if (!prev) {
        return {
          volumeDistribution: [
            {
              numberOfEnrollees: '',
              distributionDate: '',
            },
          ],
        };
      }
      return {
        ...prev,
        volumeDistribution: [
          ...(prev.volumeDistribution ?? []),
          {
            numberOfEnrollees: '',
            distributionDate: '',
          },
        ],
      };
    });
  };

  const handleChangeVolumeDistributionItem = (
    index: number,
    event: React.SyntheticEvent<HTMLInputElement> | string,
    key: keyof IVolumeDistributionAsString,
  ): void => {
    const value = (() => {
      if (typeof event === 'string') {
        return event;
      }
      const currentTarget = event.target as unknown as { value?: string };
      return currentTarget.value;
    })();
    const currentValue = values?.volumeDistribution?.[index];
    // As Values are inside and array that is nested in values we need to check
    // if the current value is different otherwise we will have an infinite loop
    // of re-renders
    if (currentValue?.distributionDate === value) {
      return;
    }
    setValues((prev) => {
      if (!prev) {
        return null;
      }
      const newVolumeDistribution = prev.volumeDistribution?.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            [key]: value,
          };
        }
        return item;
      });
      return {
        ...prev,
        volumeDistribution: newVolumeDistribution,
      };
    });
  };

  const handleRemoveVolumeDistribution = (index: number, event: React.SyntheticEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setValues((prev) => {
      if (!prev) {
        return null;
      }
      const newVolumeDistribution = prev.volumeDistribution?.filter((_, i) => i !== index);
      return {
        ...prev,
        volumeDistribution: newVolumeDistribution,
      };
    });
  };

  if (isLoading) {
    return (
      <RootContainer>
        <LoadingContainer>
          <StyledSpinnerIcon />
        </LoadingContainer>
      </RootContainer>
    );
  }

  return (
    <RootContainer>
      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (!values) {
            return;
          }
          const formValues = transformCampaignFormStringValuesToCampaignFormValues(values);
          const validationResults = validateForm(formValues, t, validateFormFn);
          const hasErrorsExceptVolumeDistribution = Object.keys(validationResults).some((key) =>
            key !== 'volumeDistribution' ? Boolean(validationResults[key as keyof AddCampaignValueErrors]) : false,
          );
          // We will validate the volume distribution separately as it is an array
          const volumeDistributionErrors = values?.volumeDistribution?.map((value) => {
            return validateVolumeDistributionItem(value, t);
          });
          const hasVolumeDistributionErrors = volumeDistributionErrors?.some(
            (error) => error.distributionDate !== undefined || error.numberOfEnrollees !== undefined,
          );
          setErrors({ ...validationResults, volumeDistribution: volumeDistributionErrors });
          const hasErrors = hasErrorsExceptVolumeDistribution || hasVolumeDistributionErrors;
          if (!hasErrors && onSubmit) {
            onSubmit({
              ...initialValues,
              ...values,
              volumeDistribution: values?.volumeDistribution,
            });
          }
        }}
      >
        <FlexColContainer>
          {visibleElements.campaignInfo && (
            <>
              <Input
                testId="campaign-name"
                width="100%"
                id="campaignName"
                name="campaign-name"
                placeholder={copies.campaignName.placeholder}
                label={copies.campaignName.label}
                secondaryLabel={copies.campaignName.secondaryLabel}
                error={Boolean(errors.campaignName)}
                errorDescription={errors.campaignName}
                value={values?.campaignName}
                onChange={(newValue) => handleSetValue(newValue, 'campaignName')}
              />
              <RowContainer>
                <Input
                  testId="campaign-source"
                  width="100%"
                  id="campaignSource"
                  name="campaign-source"
                  placeholder={copies.campaignSource.placeholder}
                  label={copies.campaignSource.label}
                  secondaryLabel={copies.campaignSource.secondaryLabel}
                  error={Boolean(errors.campaignSource)}
                  errorDescription={errors.campaignSource}
                  value={values?.campaignSource}
                  onChange={(newValue) => handleSetValue(newValue, 'campaignSource')}
                />
                <Select
                  testId="campaign-medium"
                  itemTestId="campaign-medium-item"
                  width="100%"
                  id="campaignMedium"
                  name="campaign-medium"
                  placeholder={copies.campaignMedium.placeholder}
                  label={copies.campaignMedium.label}
                  secondaryLabel={copies.campaignMedium.secondaryLabel}
                  error={Boolean(errors.campaignMedium)}
                  errorDescription={errors.campaignMedium}
                  value={values?.campaignMedium}
                  onChange={(newValue) => handleChangeSelect(newValue, 'campaignMedium')}
                  items={campaignMedium}
                />
              </RowContainer>
              <RowContainer>
                <Input
                  width="100%"
                  testId="campaign-term"
                  id="campaignTerm"
                  name="campaign-term"
                  placeholder={copies.campaignTerm.placeholder}
                  label={copies.campaignTerm.label}
                  secondaryLabel={copies.campaignTerm.secondaryLabel}
                  error={Boolean(errors.campaignTerm)}
                  errorDescription={errors.campaignTerm}
                  value={values?.campaignTerm}
                  onChange={(newValue) => handleSetValue(newValue, 'campaignTerm')}
                />
                <Input
                  width="100%"
                  testId="campaign-content"
                  id="campaignContent"
                  name="campaign-content"
                  placeholder={copies.campaignContent.placeholder}
                  label={copies.campaignContent.label}
                  secondaryLabel={copies.campaignContent.secondaryLabel}
                  error={Boolean(errors.campaignContent)}
                  errorDescription={errors.campaignContent}
                  value={values?.campaignContent}
                  onChange={(newValue) => handleSetValue(newValue, 'campaignContent')}
                />
              </RowContainer>
            </>
          )}
          {visibleElements.dates && (
            <RowContainer>
              <InputDateSelector
                title={copies.campaignLaunchDate.label}
                secondaryLabel={copies.campaignLaunchDate.secondaryLabel}
                placeHolder={copies.campaignLaunchDate.placeholder}
                value={values?.launchDate ? new Date(values.launchDate) : undefined}
                inputProps={{
                  width: '100%',
                  name: 'launchDate',
                  id: 'launchDate',
                  error: Boolean(errors.launchDate),
                  errorDescription: errors.launchDate,
                }}
                onChange={(newValue) => {
                  const inputDateValue = newValue ? newValue.toISOString() : null;
                  handleInputDateValues(inputDateValue, 'launchDate');
                }}
              />
              <InputDateSelector
                title={copies.campaignEndDate.label}
                secondaryLabel={copies.campaignEndDate.secondaryLabel}
                placeHolder={copies.campaignEndDate.placeholder}
                value={values?.endDate ? new Date(values.endDate) : undefined}
                inputProps={{
                  width: '100%',
                  name: 'endDate',
                  id: 'endDate',
                  error: Boolean(errors.endDate),
                  errorDescription: errors.endDate,
                }}
                onChange={(newValue) => {
                  const inputDateValue = newValue ? newValue.toISOString() : null;
                  handleInputDateValues(inputDateValue, 'endDate');
                }}
              />
            </RowContainer>
          )}
        </FlexColContainer>
        {url && (
          <URLContainer>
            <Label>{url}</Label>
          </URLContainer>
        )}
        {visibleElements.volumeDistribution && (
          <VolumeDistributionContainer>
            <VolumeDistribution
              t={t}
              onAddVolumeDistribution={handleAddVolumeDistribution}
              onRemoveVolumeDistribution={handleRemoveVolumeDistribution}
              onChangeVolumeDistributionItem={handleChangeVolumeDistributionItem}
              volumeDistribution={values?.volumeDistribution}
              errors={errors.volumeDistribution}
            />
          </VolumeDistributionContainer>
        )}
        <ButtonsContainer>
          <Button type="submit" css={{ margin: 'auto', marginTop: '$8' }} size={Sizes.LARGE}>
            {mainButtonLabel ?? copies.options.submit}
          </Button>
          <Button
            onClick={onCancel}
            css={{ margin: 'auto', marginTop: '$8' }}
            variant={Variants.TERTIARY}
            size={Sizes.LARGE}
            id="volume-distribution-submit"
          >
            {copies.options.cancel}
          </Button>
        </ButtonsContainer>
      </form>
    </RootContainer>
  );
};
